import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUserPlus,
  faFileCsv,
  faUserEdit,
  faChalkboardTeacher,
  faUsers,
  faUserCheck,
  faClock,
  faChartLine,
  faArrowRight,
  faInfoCircle,
  faGraduationCap,
  faCalendarAlt,
  faStar,
  faCertificate,
  faTasks,
  faSchool,
  faUserGraduate,
  faExclamationTriangle,
  faSpinner,
  faChartPie,
  faMobileAlt,
  faBookOpen,
  faRibbon,
  faChalkboard,
  faClipboardList,
  faPercent,
  faEye,
  faEyeSlash,
  faShieldAlt,
  faDatabase,
  faSync,
  faNetworkWired,
  faWaveSquare,
  faRocket
} from "@fortawesome/free-solid-svg-icons";
import "./manageTeachers.css";
import Sidebar from "./Sidebar";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const ManageTeachers = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalTeachers: 0,
    activeTeachers: 0,
    onLeave: 0,
    newThisMonth: 0,
    pendingApprovals: 0,
    avgExperience: 0,
  });

  const [distributions, setDistributions] = useState({
    departments: [],
    educationLevels: [],
    teachingLevels: [],
    status: []
  });

  const [analytics, setAnalytics] = useState({
    mobileCoverage: { withPrimaryMobile: 0, withSecondaryMobile: 0, total: 0 },
    totalDepartments: 0,
    totalEducationLevels: 0
  });

  const [performance, setPerformance] = useState({
    activeRate: 0,
    onLeaveRate: 0,
    newHireRate: 0
  });

  const [isLoading, setIsLoading] = useState(true);
  const [activeCard, setActiveCard] = useState(null);
  const [error, setError] = useState(null);

  // Toggle for showing/hiding details (default hidden)
  const [showDetails, setShowDetails] = useState(false);

  // Fetch teacher stats from API
  useEffect(() => {
    const fetchTeacherData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        // Fetch dashboard stats
        const response = await axios.get(
          "https://mhsdev.online/MHS-WEB/api/TeacherSummary.php?endpoint=dashboard"
        );

        console.log("API Response:", response.data); // Debug log

        if (response.data.success) {
          const data = response.data;
          setStats(data.stats || {});
          setDistributions(data.distributions || {});
          setAnalytics(data.analytics || {});
          setPerformance(data.performance || {});
        } else {
          setError(response.data.error || "Failed to fetch data");
          // Use sample data as fallback
          setSampleData();
        }
      } catch (error) {
        console.error("Error fetching teacher data:", error);
        setError("Unable to connect to server. Using sample data.");
        // Use sample data as fallback
        setSampleData();
      } finally {
        setIsLoading(false);
      }
    };

    const setSampleData = () => {
      setStats({
        totalTeachers: 7,
        activeTeachers: 7,
        onLeave: 0,
        newThisMonth: 0,
        pendingApprovals: 0,
        avgExperience: 14.5,
      });
      setDistributions({
        departments: [
          { department: "Class 9", count: "2" },
          { department: "Class 6", count: "1" },
          { department: "Class 7", count: "1" },
          { department: "Class 11", count: "1" },
          { department: "Class 8", count: "1" }
        ],
        educationLevels: [
          { education_level: "BS-16 Years", count: "2" },
          { education_level: "M-Phill", count: "1" },
          { education_level: "Intermediate", count: "1" },
          { education_level: "Bachelor", count: "1" },
          { education_level: "Master", count: "1" }
        ],
        teachingLevels: [
          { teaching_level: "Science Subjects", count: "3" },
          { teaching_level: "Arts Subject", count: "2" },
          { teaching_level: "Hybrid", count: "2" }
        ],
        status: [{ status: "Active", count: "7" }]
      });
      setAnalytics({
        mobileCoverage: { withPrimaryMobile: 7, withSecondaryMobile: 3, total: 7 },
        totalDepartments: 5,
        totalEducationLevels: 5
      });
      setPerformance({
        activeRate: 100,
        onLeaveRate: 0,
        newHireRate: 0
      });
    };

    fetchTeacherData();
  }, []);

  const actionCards = [
    {
      id: 1,
      title: "Add Teacher Manually",
      subtitle: "Register individual teacher profiles",
      icon: faUserPlus,
      path: "/Add-Teacher",
      colorClass: "manage-teachers-card-add",
      description: "Create detailed teacher profiles with qualifications",
      gradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    },
    {
      id: 2,
      title: "Add Teachers from CSV",
      subtitle: "Bulk import teacher records",
      icon: faFileCsv,
      path: "/addTeachersCSV",
      colorClass: "manage-teachers-card-csv",
      description: "Upload CSV files for mass teacher registration",
      gradient: "linear-gradient(135deg, #38a169 0%, #2f855a 100%)",
    },
    {
      id: 3,
      title: "Update Teachers",
      subtitle: "Modify existing teacher records",
      icon: faUserEdit,
      path: "/Edit-Teachers-Data",
      colorClass: "manage-teachers-card-edit",
      description: "Update and manage teacher information",
      gradient: "linear-gradient(135deg, #ed8936 0%, #dd6b20 100%)",
    },
    {
      id: 4,
      title: "Assign Subjects",
      subtitle: "Manage subject assignments",
      icon: faGraduationCap,
      path: "/Subject Management",
      colorClass: "manage-teachers-card-subjects",
      description: "Assign subjects and classes to teachers",
      gradient: "linear-gradient(135deg, #4299e1 0%, #3182ce 100%)",
    },
    {
      id: 5,
      title: "Teacher Schedules",
      subtitle: "Create and manage timetables",
      icon: faCalendarAlt,
      path: "/teacher-schedules",
      colorClass: "manage-teachers-card-schedule",
      description: "Manage teacher schedules and timetables",
      gradient: "linear-gradient(135deg, #9f7aea 0%, #805ad5 100%)",
    },
    {
      id: 6,
      title: "Performance Review",
      subtitle: "Track teacher performance",
      icon: faStar,
      path: "/performance-review",
      colorClass: "manage-teachers-card-review",
      description: "Conduct and review teacher performance",
      gradient: "linear-gradient(135deg, #f56565 0%, #e53e3e 100%)",
    },
  ];

  const handleCardClick = (path, id) => {
    setActiveCard(id);
    setTimeout(() => {
      navigate(path);
    }, 300);
  };

  const handleCardHover = (id) => {
    setActiveCard(id);
  };

  const handleCardLeave = () => {
    setActiveCard(null);
  };

  // Calculate percentages for distribution bars
  const calculatePercentage = (count) => {
    return (parseInt(count) / stats.totalTeachers) * 100;
  };

  if (isLoading) {
    return (
      <div className="manage-teachers-dashboard-wrapper">
        <Sidebar />
        <div className="manage-teachers-main-content manage-teachers-loading-state">
          <div className="manage-teachers-loader-spinner manage-teachers-large"></div>
          <p>Loading teacher dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="manage-teachers-dashboard-wrapper">
      <Sidebar />

      <div className="manage-teachers-main-content">
        {/* Professional Header with Toggle */}
        <header
          style={{
            backgroundColor: "#ffffff",
            padding: "20px 24px",
            borderRadius: "12px",
            border: "1px solid #e5e7eb",
            marginBottom: "24px"
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: "20px"
            }}
          >
            {/* Left Content */}
            <div>
              <h1
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  margin: 0,
                  fontSize: "22px",
                  fontWeight: "600",
                  color: "#111827"
                }}
              >
                <FontAwesomeIcon
                  icon={faChalkboardTeacher}
                  style={{ color: "#2563eb", fontSize: "20px" }}
                />
                Manage Teachers
              </h1>

              <p
                style={{
                  marginTop: "6px",
                  fontSize: "14px",
                  color: "#6b7280"
                }}
              >
                Comprehensive teacher management system
              </p>
            </div>

            {/* Right Badge + Toggle */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px"
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "8px 14px",
                  backgroundColor: "#f0f9ff",
                  border: "1px solid #bae6fd",
                  borderRadius: "999px",
                  fontSize: "13px",
                  fontWeight: "500",
                  color: "#0369a1",
                  whiteSpace: "nowrap"
                }}
              >
                <FontAwesomeIcon icon={faChartLine} />
                <span>Teacher Dashboard</span>
              </div>

              {/* Toggle Button */}
              <button
                className="manage-teachers-toggle-details-btn"
                onClick={() => setShowDetails(!showDetails)}
                title={showDetails ? "Hide details" : "Show details"}
                style={{
                  background: "linear-gradient(135deg, #6c5ce7, #a29bfe)",
                  border: "none",
                  color: "#fff",
                  padding: "8px 16px",
                  borderRadius: "20px",
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "8px",
                  fontSize: "14px",
                  fontWeight: "500",
                  transition: "all 0.3s ease",
                  boxShadow: "0 2px 8px rgba(108, 92, 231, 0.3)"
                }}
              >
                <FontAwesomeIcon icon={showDetails ? faEyeSlash : faEye} />
                <span>{showDetails ? "Hide Details" : "Show Details"}</span>
              </button>
            </div>
          </div>
        </header>

        {error && (
          <div className="manage-teachers-error-alert">
            <FontAwesomeIcon icon={faExclamationTriangle} />
            <span>{error}</span>
          </div>
        )}

        {/* ======== DETAILS SECTION (hidden by default) ======== */}
        {showDetails && (
          <>
            {/* Quick Stats */}
            <div className="manage-teachers-quick-stats">
              <div className="manage-teachers-stat-card">
                <div
                  className="manage-teachers-stat-icon"
                  style={{
                    background: "rgba(102, 126, 234, 0.1)",
                    color: "#667eea",
                  }}
                >
                  <FontAwesomeIcon icon={faUsers} />
                </div>
                <div className="manage-teachers-stat-value">{stats.totalTeachers}</div>
                <div className="manage-teachers-stat-label">Total Teachers</div>
                <div className="manage-teachers-stat-trend">
                  +{stats.newThisMonth} this month
                </div>
              </div>
              <div className="manage-teachers-stat-card">
                <div
                  className="manage-teachers-stat-icon"
                  style={{
                    background: "rgba(56, 161, 105, 0.1)",
                    color: "#38a169",
                  }}
                >
                  <FontAwesomeIcon icon={faUserCheck} />
                </div>
                <div className="manage-teachers-stat-value">{stats.activeTeachers}</div>
                <div className="manage-teachers-stat-label">Active</div>
                <div className="manage-teachers-stat-trend">
                  {performance.activeRate}% active rate
                </div>
              </div>
              <div className="manage-teachers-stat-card">
                <div
                  className="manage-teachers-stat-icon"
                  style={{
                    background: "rgba(237, 137, 54, 0.1)",
                    color: "#ed8936",
                  }}
                >
                  <FontAwesomeIcon icon={faClock} />
                </div>
                <div className="manage-teachers-stat-value">{stats.onLeave}</div>
                <div className="manage-teachers-stat-label">On Leave</div>
                <div className="manage-teachers-stat-trend">
                  {performance.onLeaveRate}% of total
                </div>
              </div>
              <div className="manage-teachers-stat-card">
                <div
                  className="manage-teachers-stat-icon"
                  style={{
                    background: "rgba(66, 153, 225, 0.1)",
                    color: "#4299e1",
                  }}
                >
                  <FontAwesomeIcon icon={faCertificate} />
                </div>
                <div className="manage-teachers-stat-value">{stats.avgExperience}yrs</div>
                <div className="manage-teachers-stat-label">Avg Experience</div>
                <div className="manage-teachers-stat-trend">Industry leading</div>
              </div>
            </div>

            {/* Performance Metrics */}
            <div className="manage-teachers-performance-metrics">
              <div className="manage-teachers-section-header">
                <h3>
                  <FontAwesomeIcon icon={faRibbon} />
                  Performance Metrics
                </h3>
              </div>
              <div className="manage-teachers-metrics-grid">
                <div className="manage-teachers-metric-card">
                  <div className="manage-teachers-metric-icon">
                    <FontAwesomeIcon icon={faPercent} />
                  </div>
                  <div className="manage-teachers-metric-content">
                    <div className="manage-teachers-metric-value">{performance.activeRate}%</div>
                    <div className="manage-teachers-metric-label">Active Rate</div>
                  </div>
                  <div className="manage-teachers-metric-progress">
                    <div 
                      className="manage-teachers-metric-progress-bar"
                      style={{ width: `${performance.activeRate}%` }}
                    ></div>
                  </div>
                </div>
                <div className="manage-teachers-metric-card">
                  <div className="manage-teachers-metric-icon">
                    <FontAwesomeIcon icon={faClipboardList} />
                  </div>
                  <div className="manage-teachers-metric-content">
                    <div className="manage-teachers-metric-value">{stats.newThisMonth}</div>
                    <div className="manage-teachers-metric-label">New This Month</div>
                  </div>
                  <div className="manage-teachers-metric-progress">
                    <div 
                      className="manage-teachers-metric-progress-bar"
                      style={{ width: `${performance.newHireRate}%` }}
                    ></div>
                  </div>
                </div>
                <div className="manage-teachers-metric-card">
                  <div className="manage-teachers-metric-icon">
                    <FontAwesomeIcon icon={faChalkboard} />
                  </div>
                  <div className="manage-teachers-metric-content">
                    <div className="manage-teachers-metric-value">{analytics.totalDepartments}</div>
                    <div className="manage-teachers-metric-label">Total Departments</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Distributions Section */}
            <div className="manage-teachers-distributions-section">
              <div className="manage-teachers-section-header">
                <h3>
                  <FontAwesomeIcon icon={faChartPie} />
                  Teacher Distributions
                </h3>
              </div>
              
              <div className="manage-teachers-distributions-grid">
                {/* Departments Distribution */}
                <div className="manage-teachers-distribution-card">
                  <div className="manage-teachers-distribution-header">
                    <FontAwesomeIcon icon={faBookOpen} />
                    <h4>Departments</h4>
                    <span className="manage-teachers-distribution-count">
                      {analytics.totalDepartments} Departments
                    </span>
                  </div>
                  <div className="manage-teachers-distribution-list">
                    {distributions.departments?.slice(0, 5).map((dept, index) => (
                      <div key={index} className="manage-teachers-distribution-item">
                        <div className="manage-teachers-distribution-name">{dept.department}</div>
                        <div className="manage-teachers-distribution-bar">
                          <div 
                            className="manage-teachers-distribution-fill"
                            style={{
                              width: `${calculatePercentage(dept.count)}%`,
                              background: "#667eea"
                            }}
                          ></div>
                        </div>
                        <div className="manage-teachers-distribution-value">{dept.count} teachers</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Education Levels Distribution */}
                <div className="manage-teachers-distribution-card">
                  <div className="manage-teachers-distribution-header">
                    <FontAwesomeIcon icon={faGraduationCap} />
                    <h4>Education Levels</h4>
                    <span className="manage-teachers-distribution-count">
                      {analytics.totalEducationLevels} Levels
                    </span>
                  </div>
                  <div className="manage-teachers-distribution-list">
                    {distributions.educationLevels?.slice(0, 5).map((edu, index) => (
                      <div key={index} className="manage-teachers-distribution-item">
                        <div className="manage-teachers-distribution-name">{edu.education_level}</div>
                        <div className="manage-teachers-distribution-bar">
                          <div 
                            className="manage-teachers-distribution-fill"
                            style={{
                              width: `${calculatePercentage(edu.count)}%`,
                              background: "#38a169"
                            }}
                          ></div>
                        </div>
                        <div className="manage-teachers-distribution-value">{edu.count} teachers</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Teaching Levels Distribution */}
                <div className="manage-teachers-distribution-card">
                  <div className="manage-teachers-distribution-header">
                    <FontAwesomeIcon icon={faChalkboardTeacher} />
                    <h4>Teaching Levels</h4>
                    <span className="manage-teachers-distribution-count">
                      {distributions.teachingLevels?.length || 0} Categories
                    </span>
                  </div>
                  <div className="manage-teachers-distribution-list">
                    {distributions.teachingLevels?.map((level, index) => (
                      <div key={index} className="manage-teachers-distribution-item">
                        <div className="manage-teachers-distribution-name">{level.teaching_level}</div>
                        <div className="manage-teachers-distribution-bar">
                          <div 
                            className="manage-teachers-distribution-fill"
                            style={{
                              width: `${calculatePercentage(level.count)}%`,
                              background: "#9f7aea"
                            }}
                          ></div>
                        </div>
                        <div className="manage-teachers-distribution-value">{level.count} teachers</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Mobile Coverage */}
                <div className="manage-teachers-distribution-card">
                  <div className="manage-teachers-distribution-header">
                    <FontAwesomeIcon icon={faMobileAlt} />
                    <h4>Mobile Coverage</h4>
                    <span className="manage-teachers-distribution-count">
                      {analytics.mobileCoverage.total} Active
                    </span>
                  </div>
                  <div className="manage-teachers-mobile-stats">
                    <div className="manage-teachers-mobile-stat">
                      <div className="manage-teachers-mobile-icon" style={{background: "rgba(66, 153, 225, 0.1)"}}>
                        <FontAwesomeIcon icon={faMobileAlt} />
                      </div>
                      <div className="manage-teachers-mobile-content">
                        <div className="manage-teachers-mobile-value">
                          {analytics.mobileCoverage.withPrimaryMobile}
                        </div>
                        <div className="manage-teachers-mobile-label">Primary Mobile</div>
                        <div className="manage-teachers-mobile-percentage">
                          {Math.round((analytics.mobileCoverage.withPrimaryMobile / analytics.mobileCoverage.total) * 100)}% coverage
                        </div>
                      </div>
                    </div>
                    <div className="manage-teachers-mobile-stat">
                      <div className="manage-teachers-mobile-icon" style={{background: "rgba(159, 122, 234, 0.1)"}}>
                        <FontAwesomeIcon icon={faMobileAlt} />
                      </div>
                      <div className="manage-teachers-mobile-content">
                        <div className="manage-teachers-mobile-value">
                          {analytics.mobileCoverage.withSecondaryMobile}
                        </div>
                        <div className="manage-teachers-mobile-label">Secondary Mobile</div>
                        <div className="manage-teachers-mobile-percentage">
                          {Math.round((analytics.mobileCoverage.withSecondaryMobile / analytics.mobileCoverage.total) * 100)}% coverage
                        </div>
                      </div>
                    </div>
                    <div className="manage-teachers-coverage-summary">
                      <div className="manage-teachers-coverage-item">
                        <span>Total Teachers:</span>
                        <span className="manage-teachers-coverage-value">{analytics.mobileCoverage.total}</span>
                      </div>
                      <div className="manage-teachers-coverage-item">
                        <span>Full Coverage:</span>
                        <span className="manage-teachers-coverage-value">
                          {Math.round((analytics.mobileCoverage.withPrimaryMobile / analytics.mobileCoverage.total) * 100)}%
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Status Summary */}
            <div className="manage-teachers-status-summary">
              <div className="manage-teachers-section-header">
                <h3>
                  <FontAwesomeIcon icon={faClipboardList} />
                  Status Summary
                </h3>
              </div>
              <div className="manage-teachers-status-grid">
                {distributions.status?.map((statusItem, index) => (
                  <div key={index} className="manage-teachers-status-card">
                    <div className="manage-teachers-status-icon">
                      <FontAwesomeIcon icon={faUserCheck} />
                    </div>
                    <div className="manage-teachers-status-content">
                      <div className="manage-teachers-status-value">{statusItem.count}</div>
                      <div className="manage-teachers-status-label">{statusItem.status}</div>
                      <div className="manage-teachers-status-percentage">
                        {Math.round((parseInt(statusItem.count) / stats.totalTeachers) * 100)}%
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Alerts Section */}
            <div className="manage-teachers-alerts-section">
              <div className="manage-teachers-alert-header">
                <FontAwesomeIcon icon={faExclamationTriangle} />
                <h4>Pending Actions & Insights</h4>
              </div>
              <div className="manage-teachers-alerts-grid">
                <div className="manage-teachers-alert-item">
                  <div className="manage-teachers-alert-badge">{stats.pendingApprovals}</div>
                  <div className="manage-teachers-alert-content">
                    <div className="manage-teachers-alert-title">Teacher Approvals</div>
                    <div className="manage-teachers-alert-desc">
                      New teacher profiles awaiting approval
                    </div>
                  </div>
                </div>
                <div className="manage-teachers-alert-item">
                  <div className="manage-teachers-alert-badge">{stats.newThisMonth}</div>
                  <div className="manage-teachers-alert-content">
                    <div className="manage-teachers-alert-title">New Registrations</div>
                    <div className="manage-teachers-alert-desc">
                      Teachers registered this month
                    </div>
                  </div>
                </div>
                <div className="manage-teachers-alert-item">
                  <div className="manage-teachers-alert-badge">{stats.onLeave}</div>
                  <div className="manage-teachers-alert-content">
                    <div className="manage-teachers-alert-title">Teachers on Leave</div>
                    <div className="manage-teachers-alert-desc">
                      Currently unavailable teachers
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* ======== ACTIONS (always visible) ======== */}
        <section className="manage-teachers-actions-section manage-teachers-fade-in">
          <h2 className="manage-teachers-section-title">
            <FontAwesomeIcon icon={faTasks} className="manage-teachers-section-icon" />
            Teacher Management Actions
          </h2>

          <div className="manage-teachers-actions-grid">
            {actionCards.map((card) => (
              <div
                key={card.id}
                className={`manage-teachers-action-card ${card.colorClass} ${
                  activeCard === card.id ? "manage-teachers-active" : ""
                }`}
                onClick={() => handleCardClick(card.path, card.id)}
                onMouseEnter={() => handleCardHover(card.id)}
                onMouseLeave={handleCardLeave}
                tabIndex="0"
                role="button"
                aria-label={`Navigate to ${card.title}`}
              >
                <div
                  className="manage-teachers-action-icon"
                  style={{ background: card.gradient }}
                >
                  <FontAwesomeIcon icon={card.icon} className="manage-teachers-card-icon" />
                </div>

                <h3 className="manage-teachers-action-title">{card.title}</h3>
                <p className="manage-teachers-action-subtitle">{card.subtitle}</p>

                <div
                  className="manage-teachers-card-action-hint"
                  style={{
                    opacity: activeCard === card.id ? 1 : 0,
                  }}
                >
                  <span>Explore</span>
                  <FontAwesomeIcon icon={faArrowRight} />
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Quick Guide Section (always visible) */}
        <div className="manage-teachers-guide-section">
          <h3>
            <FontAwesomeIcon icon={faInfoCircle} />
            Quick Guide
          </h3>
          <div className="manage-teachers-guide-grid">
            <div className="manage-teachers-guide-item">
              <div
                className="manage-teachers-guide-icon"
                style={{
                  background: "rgba(102, 126, 234, 0.1)",
                  color: "#667eea",
                }}
              >
                <FontAwesomeIcon icon={faUserPlus} />
              </div>
              <div>
                <div className="manage-teachers-guide-title">Individual Registration</div>
                <div className="manage-teachers-guide-desc">
                  Use "Add Teacher Manually" for detailed profile creation
                </div>
              </div>
            </div>
            <div className="manage-teachers-guide-item">
              <div
                className="manage-teachers-guide-icon"
                style={{
                  background: "rgba(56, 161, 105, 0.1)",
                  color: "#38a169",
                }}
              >
                <FontAwesomeIcon icon={faFileCsv} />
              </div>
              <div>
                <div className="manage-teachers-guide-title">Bulk Operations</div>
                <div className="manage-teachers-guide-desc">
                  Import multiple teachers via CSV for efficient onboarding
                </div>
              </div>
            </div>
            <div className="manage-teachers-guide-item">
              <div
                className="manage-teachers-guide-icon"
                style={{
                  background: "rgba(237, 137, 54, 0.1)",
                  color: "#ed8936",
                }}
              >
                <FontAwesomeIcon icon={faGraduationCap} />
              </div>
              <div>
                <div className="manage-teachers-guide-title">Subject Assignment</div>
                <div className="manage-teachers-guide-desc">
                  Assign subjects and classes to optimize teaching loads
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ======== NEW FOOTER (same design as ManageStudents) ======== */}
        <footer className="manage-teachers-footer">
          <div className="manage-teachers-footer-content">
            <div className="manage-teachers-footer-brand">
              <div className="manage-teachers-brand-logo">
                <FontAwesomeIcon icon={faShieldAlt} />
              </div>
              <div className="manage-teachers-brand-text">
                <div className="manage-teachers-brand-name">Teacher Management System</div>
                <div className="manage-teachers-brand-version">Professional Edition v2.2.0</div>
              </div>
            </div>
            <div className="manage-teachers-footer-details">
              <div className="manage-teachers-detail-item">
                <FontAwesomeIcon icon={faSync} />
                <span>Last updated: Today</span>
              </div>
              <div className="manage-teachers-detail-item">
                <FontAwesomeIcon icon={faDatabase} />
                <span>{stats.totalTeachers} Active Teachers</span>
              </div>
              <div className="manage-teachers-detail-item">
                <FontAwesomeIcon icon={faUsers} />
                <span>{stats.activeTeachers} Currently Active</span>
              </div>
            </div>
          </div>
          <div className="manage-teachers-footer-copyright">
            <p>© {new Date().getFullYear()} My School. All Rights Reserved.</p>
            <div className="manage-teachers-footer-links">
              <span>Privacy Policy</span>
              <span>•</span>
              <span>Terms of Service</span>
              <span>•</span>
              <span>Support</span>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default ManageTeachers;